import { useAction, useAtom } from '@reatom/npm-react'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { CurrencyModel } from '@root/entities/currency'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { useWalletAssetBalance } from '@root/entities/wallet/hooks'
import { tradingWalletAtom } from '@root/features/trade/trading'
import { AmountFormatted } from '@root/shared/core/format'

import { openInstantTradeDialogAction } from '../../atoms'
import { reatomInstantTradeBalance } from '../../atoms/reatom-instant-trade-usd-balance'

import type { PairModel } from '@root/entities/pair'
import type { TradeType } from '@root/entities/trade-type'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  swapDirection: TradeType.SwapDirection
  pair: PairModel
} & ClassNameProps

export const InstantTradeWalletBalance: React.FC<Props> = ({
  swapDirection,
  pair,
  className,
}) => {
  const [wallet] = useAtom(tradingWalletAtom)
  const currency = CurrencyModel.toNativeIfWrapped(
    swapDirection === 'buy' ? pair.base : pair.quote,
  )
  const setTab = useAction(openInstantTradeDialogAction)

  const usdBalanceAtom = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomInstantTradeBalance({ swapDirection, pair }),
    [pair, swapDirection],
  )
  const [balanceInUsd] = useAtom(usdBalanceAtom)
  const balance = useWalletAssetBalance({
    currency,
    wallet,
  })

  const handleWalletsOpen = () => {
    setTab('wallets')
  }

  return (
    <WalletBalance className={className} onClick={handleWalletsOpen}>
      <AmountFormatted
        amount={balanceInUsd}
        currency="$"
        roundingMode="trunc"
      />
      |
      <AmountFormatted
        amount={balance}
        currency={currency.ticker}
        roundingMode="trunc"
      />
      <CurrencyIcon currency={currency} size={14} withGoogleLens={false} />
    </WalletBalance>
  )
}

const WalletBalance = styled.div`
  --wallet-balance-height: 22px;
  background: var(--background-3);
  color: var(--text-11);

  cursor: pointer;
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 4px;

  padding: 2px 5px;
  height: var(--wallet-balance-height);
  border-radius: 6px;

  font-size: 11px;
  font-weight: 500;
  line-height: 16px;

  transition: background 0.2s ease-in-out;
  &:hover {
    background: var(--background-4);
  }
`
